/**
 * PersonPicker — find somebody the ecosystem already holds before typing them
 * in again.
 *
 * Searches church members, event delegates, students, applicants, staff and
 * previous pledgers in one go and shows which modules hold a record. Picking
 * somebody links the new pledge to that person; it never alters their
 * membership, registration or enrolment. Contact details arrive masked — this
 * is identity, not a mailing list.
 */
import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, Loader2, UserCheck, X } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PersonPicker = ({ onSelect, selected, onClear, label = 'Find an existing person' }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (term.trim().length < 2) { setResults([]); setSearched(false); return undefined; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API_URL}/api/people/search?q=${encodeURIComponent(term.trim())}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = res.ok ? await res.json() : { results: [] };
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
        setSearched(true);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [term]);

  if (selected) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="person-picker-selected">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <UserCheck className="h-4 w-4 text-emerald-700" /> {selected.name}
            </p>
            <p className="mt-0.5 text-xs text-slate-600">
              {(selected.modules || []).join(' · ')}
              {selected.references?.length ? ` · ${selected.references.join(', ')}` : ''}
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              Linked to their existing record — nothing about their membership, registration or enrolment changes.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { onClear?.(); setTerm(''); }}
                  data-testid="person-picker-clear">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="person-picker">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={`${label} — name, email, phone, membership or student number`}
          className="pl-9"
          data-testid="person-picker-input"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>

      {results.length > 0 && (
        <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-1"
             data-testid="person-picker-results">
          {results.map((person) => (
            <button
              key={person.identity_key}
              type="button"
              onClick={() => { onSelect(person); setResults([]); setTerm(''); }}
              className="w-full rounded-md px-3 py-2 text-left transition-colors hover:bg-indigo-50"
              data-testid={`person-result-${person.identity_key}`}
            >
              <p className="text-sm font-medium text-slate-900">{person.name}</p>
              <p className="text-xs text-slate-500">
                {person.email_masked || person.phone_masked || 'no contact on record'}
                {person.references?.length ? ` · ${person.references[0]}` : ''}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {(person.modules || []).map((m) => (
                  <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {searched && !searching && results.length === 0 && (
        <p className="text-xs text-slate-500" data-testid="person-picker-empty">
          Nobody on record matches that. Fill the details in below to create a new record —
          two people really can share a name, so nothing is linked on a name alone.
        </p>
      )}
    </div>
  );
};

export default PersonPicker;
